import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Search, FilterX, User, Plus, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useStudentMatchSearch, useStudentByCode } from "@/hooks/useStudentMatchSearch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { TeacherMatchFilters as FiltersType, AvailabilitySlot } from "@/hooks/useTeacherMatch";

interface FilterOptions {
  subjects: string[];
  educationStages: string[];
  courseTypes: string[];
  specialized: string[];
  teachingStyles: string[];
  styleLevels: number[];
  gender: string[];
  personality: string[];
  trialLesson: string[];
  branches: string[];
  studentProficiency: string[];
}

interface Props {
  filters: FiltersType;
  filterOptions: FilterOptions | null;
  onUpdateFilters: (updates: Partial<FiltersType>) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  resultCount: number;
  totalCount: number;
}

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Show a red asterisk after the label to mark this filter as required. */
  required?: boolean;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({ label, options, selected, onChange, required }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-w-[140px] px-3 py-2 text-sm border rounded-md bg-background hover:bg-accent text-left flex items-center justify-between"
      >
        <span className={selected.length ? "text-foreground" : "text-muted-foreground"}>
          {selected.length ? `${label} (${selected.length})` : label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </span>
        <svg className="h-4 w-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-popover border rounded-md shadow-lg max-h-60 overflow-y-auto">
            {options.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={e => {
                    if (e.target.checked) onChange([...selected, opt]);
                    else onChange(selected.filter(s => s !== opt));
                  }}
                  className="rounded border-input"
                />
                {opt}
              </label>
            ))}
            {options.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">ไม่มีข้อมูล</div>}
          </div>
        </>
      )}
    </div>
  );
};

/** Inline checkbox group for small option sets (≤5 items) */
const CheckboxGroupFilter: React.FC<{
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  orientation?: "horizontal" | "vertical";
  hideLabel?: boolean;
}> = ({ label, options, selected, onChange, orientation = "horizontal", hideLabel = false }) => {
  const toggle = (opt: string) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };

  return (
    <div className="space-y-1.5">
      {!hideLabel && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      <div className={orientation === "vertical" ? "flex flex-col gap-1.5" : "flex flex-wrap gap-2"}>
        {options.map(opt => (
          <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-sm">
            <Checkbox checked={selected.includes(opt)} onCheckedChange={() => toggle(opt)} />
            <span className={selected.includes(opt) ? "text-foreground font-medium" : "text-muted-foreground"}>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

/** Inline checkbox group for number arrays */
const NumberCheckboxGroupFilter: React.FC<{
  label: string;
  options: number[];
  selected: number[];
  onChange: (values: number[]) => void;
  renderLabel?: (v: number) => string;
  orientation?: "horizontal" | "vertical";
  hideLabel?: boolean;
}> = ({ label, options, selected, onChange, renderLabel, orientation = "horizontal", hideLabel = false }) => {
  const toggle = (opt: number) => {
    if (selected.includes(opt)) onChange(selected.filter(s => s !== opt));
    else onChange([...selected, opt]);
  };

  return (
    <div className="space-y-1.5">
      {!hideLabel && <span className="text-xs font-medium text-muted-foreground">{label}</span>}
      <div className={orientation === "vertical" ? "flex flex-col gap-1.5" : "flex flex-wrap gap-2"}>
        {options.map(opt => (
          <label key={opt} className="flex items-center gap-1.5 cursor-pointer text-sm">
            <Checkbox checked={selected.includes(opt)} onCheckedChange={() => toggle(opt)} />
            <span className={selected.includes(opt) ? "text-foreground font-medium" : "text-muted-foreground"}>
              {renderLabel ? renderLabel(opt) : String(opt)}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const TeacherMatchFilters: React.FC<Props> = ({
  filters,
  filterOptions,
  onUpdateFilters,
  onClearFilters,
  hasActiveFilters,
  resultCount,
  totalCount
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);
  const [universityInput, setUniversityInput] = useState(filters.university);
  const [majorInput, setMajorInput] = useState(filters.major);
  const [studentOpen, setStudentOpen] = useState(false);
  const [studentQuery, setStudentQuery] = useState("");
  const { data: studentResults = [], isFetching: studentLoading } = useStudentMatchSearch(studentQuery);
  const { data: selectedStudent } = useStudentByCode(filters.selectedStudentCode);
  const [trialOpen, setTrialOpen] = useState(false);
  const [trialQuery, setTrialQuery] = useState("");
  const { data: trialResults = [], isFetching: trialLoading } = useQuery({
    enabled: trialQuery.trim().length >= 1,
    queryKey: ["teacher-match-trial-search", trialQuery],
    queryFn: async () => {
      const s = `%${trialQuery.trim()}%`;
      const { data, error } = await supabase
        .from("class_trials")
        .select("id, student_firstname, student_nickname, student_name")
        .or(`student_firstname.ilike.${s},student_nickname.ilike.${s},student_name.ilike.${s}`)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data || [];
    }
  });
  const { data: selectedTrial } = useQuery({
    enabled: !!filters.selectedClassTrialId,
    queryKey: ["teacher-match-trial-by-id", filters.selectedClassTrialId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("class_trials")
        .select(
          "id, student_firstname, student_nickname, student_name, class_type, subject, grade, location, language, specialized_tutoring, teaching_style"
        )
        .eq("id", filters.selectedClassTrialId)
        .maybeSingle();
      if (error) throw error;
      return data;
    }
  });

  // Auto-fill courseTypes / interSubTypes when the selected student changes.
  // - trial mode: derive courseType from class_trials.class_type (e.g. "Premiere/TH" -> Regular)
  // - system / manual / none: reset to empty (uncheck)
  // Always reset interSubTypes to [] on student change to avoid stale value carrying over.
  useEffect(() => {
    let newCourseTypes: string[] = [];
    if (filters.studentMode === "trial" && selectedTrial?.class_type) {
      const ct = selectedTrial.class_type as string;
      if (ct.endsWith("/TH")) newCourseTypes = ["Regular"];
      else if (ct.endsWith("/Inter")) newCourseTypes = ["Inter"];
    }
    const curCT = [...filters.courseTypes].sort().join(",");
    const newCT = [...newCourseTypes].sort().join(",");
    const interEmpty = filters.interSubTypes.length === 0;
    if (curCT !== newCT || !interEmpty) {
      onUpdateFilters({ courseTypes: newCourseTypes, interSubTypes: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.studentMode,
    filters.selectedStudentCode,
    filters.selectedClassTrialId,
    filters.selectedStudentDisplayName,
    selectedTrial?.class_type
  ]);

  // Auto-fill `subjects` filter from class_trials.subject (trial mode only).
  // - Only sets when there's a safe match against filterOptions.subjects
  //   (case- & whitespace-insensitive). Free-text "อื่นๆ" values won't match
  //   and are left alone so the admin can choose manually.
  // - Never auto-clears: preserves the user's manual selection if no value
  //   is available, or when switching out of trial mode.
  useEffect(() => {
    if (filters.studentMode !== "trial") return;
    const trialSubject = (selectedTrial?.subject || "").trim();
    if (!trialSubject) return;
    const opts = filterOptions?.subjects || [];
    const match = opts.find(o => o.trim().toLowerCase() === trialSubject.toLowerCase());
    if (!match) return;
    if (filters.subjects.length === 1 && filters.subjects[0] === match) return;
    onUpdateFilters({ subjects: [match] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.studentMode, filters.selectedClassTrialId, selectedTrial?.subject, filterOptions?.subjects]);

  // Auto-fill `educationStages` filter from grade/education_level.
  // Works in BOTH trial mode (class_trials.grade) and system mode
  // (fishsix_students.education_level).
  //
  // Important mismatch: trial/student store INDIVIDUAL grades ("ป.1"–"ม.6"),
  // but teachers self-report STAGE GROUPS ("ประถมต้น"/"ประถมปลาย"/"ม.ต้น"/"ม.ปลาย")
  // in /salary Step 2 — so `filterOptions.educationStages` only contains the
  // group values. We map the individual grade to its group before matching.
  // Values without a mapping (e.g. "มหาวิทยาลัย", "ไม่ระบุ") pass through and
  // simply won't match → admin picks manually.
  useEffect(() => {
    if (filters.studentMode === "manual" || filters.studentMode === "none") return;
    const raw = filters.studentMode === "trial" ? (selectedTrial?.grade || "").trim() : (selectedStudent?.education_level || "").trim();
    if (!raw) return;
    const mapGradeToStage = (g: string): string => {
      const x = g.trim();
      if (["ป.1", "ป.2", "ป.3"].includes(x)) return "ประถมต้น";
      if (["ป.4", "ป.5", "ป.6"].includes(x)) return "ประถมปลาย";
      if (["ม.1", "ม.2", "ม.3"].includes(x)) return "ม.ต้น";
      if (["ม.4", "ม.5", "ม.6"].includes(x)) return "ม.ปลาย";
      return x; // pass through (may already be a group value like "ม.ปลาย")
    };
    const candidate = mapGradeToStage(raw);
    const opts = filterOptions?.educationStages || [];
    const match = opts.find(o => o.trim().toLowerCase() === candidate.toLowerCase());
    if (!match) return;
    if (filters.educationStages.length === 1 && filters.educationStages[0] === match) return;
    onUpdateFilters({ educationStages: [match] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.studentMode,
    filters.selectedClassTrialId,
    filters.selectedStudentCode,
    selectedTrial?.grade,
    selectedStudent?.education_level,
    filterOptions?.educationStages
  ]);

  // Auto-fill `branches` filter from class_trials.location (trial mode only).
  // System students don't store a branch/location, so this only fires for
  // trial mode. Safe-match against filterOptions.branches; never auto-clears.
  useEffect(() => {
    if (filters.studentMode !== "trial") return;
    const trialLocation = (selectedTrial?.location || "").trim();
    if (!trialLocation) return;
    const opts = filterOptions?.branches || [];
    const match = opts.find(o => o.trim().toLowerCase() === trialLocation.toLowerCase());
    if (!match) return;
    if (filters.branches.length === 1 && filters.branches[0] === match) return;
    onUpdateFilters({ branches: [match] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.studentMode, filters.selectedClassTrialId, selectedTrial?.location, filterOptions?.branches]);

  // Auto-fill `interSubTypes` from class_trials.language (trial mode only).
  // Maps Thai label → internal code:
  //   "เนื้อหาอังกฤษ สื่อสารอังกฤษ" → EN_EN
  //   "เนื้อหาอังกฤษ สื่อสารไทย"     → EN_TH
  //   ค่าอื่น (เนื้อหาไทย ...)        → ไม่แตะ (interSubTypes ใช้สำหรับ Inter เท่านั้น)
  useEffect(() => {
    if (filters.studentMode !== "trial") return;
    const lang = (selectedTrial?.language || "").trim();
    if (!lang) return;
    let target: string | null = null;
    if (lang === "เนื้อหาอังกฤษ สื่อสารอังกฤษ") target = "EN_EN";
    else if (lang === "เนื้อหาอังกฤษ สื่อสารไทย") target = "EN_TH";
    if (!target) return;
    if (filters.interSubTypes.length === 1 && filters.interSubTypes[0] === target) return;
    onUpdateFilters({ interSubTypes: [target] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.studentMode, filters.selectedClassTrialId, selectedTrial?.language]);

  // Auto-fill `specialized` from class_trials.specialized_tutoring.
  // Stored as JSON-string of array (e.g. '["A","B"]'). Parse, then safe-match
  // each element against filterOptions.specialized. Sets the intersection.
  useEffect(() => {
    if (filters.studentMode !== "trial") return;
    const raw = (selectedTrial as { specialized_tutoring?: string } | null | undefined)?.specialized_tutoring;
    if (!raw) return;
    let parsed: string[] = [];
    try {
      const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (Array.isArray(arr)) {
        parsed = arr.filter((x): x is string => typeof x === "string" && x.trim().length > 0);
      }
    } catch {
      return;
    }
    if (parsed.length === 0) return;
    const opts = filterOptions?.specialized || [];
    const matched = parsed.map(p => opts.find(o => o.trim().toLowerCase() === p.trim().toLowerCase())).filter((x): x is string => !!x);
    if (matched.length === 0) return;
    const cur = [...filters.specialized].sort().join("|");
    const next = [...matched].sort().join("|");
    if (cur === next) return;
    onUpdateFilters({ specialized: matched });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.studentMode,
    filters.selectedClassTrialId,
    (selectedTrial as { specialized_tutoring?: string } | null | undefined)?.specialized_tutoring,
    filterOptions?.specialized
  ]);

  // Auto-fill `teachingStyles` from class_trials.teaching_style (jsonb array).
  // Already parsed by supabase client → just safe-match each.
  useEffect(() => {
    if (filters.studentMode !== "trial") return;
    const raw = (selectedTrial as { teaching_style?: unknown } | null | undefined)?.teaching_style;
    if (!raw || !Array.isArray(raw)) return;
    const arr = raw.filter((x: unknown): x is string => typeof x === "string" && x.trim().length > 0);
    if (arr.length === 0) return;
    const opts = filterOptions?.teachingStyles || [];
    const matched = arr.map(p => opts.find(o => o.trim().toLowerCase() === p.trim().toLowerCase())).filter((x): x is string => !!x);
    if (matched.length === 0) return;
    const cur = [...filters.teachingStyles].sort().join("|");
    const next = [...matched].sort().join("|");
    if (cur === next) return;
    onUpdateFilters({ teachingStyles: matched });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.studentMode,
    filters.selectedClassTrialId,
    (selectedTrial as { teaching_style?: unknown } | null | undefined)?.teaching_style,
    filterOptions?.teachingStyles
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onUpdateFilters({ search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (universityInput !== filters.university) {
        onUpdateFilters({ university: universityInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [universityInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (majorInput !== filters.major) {
        onUpdateFilters({ major: majorInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [majorInput]);

  // Sync external changes
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);
  useEffect(() => {
    setUniversityInput(filters.university);
  }, [filters.university]);
  useEffect(() => {
    setMajorInput(filters.major);
  }, [filters.major]);

  if (!filterOptions) return null;

  const WEEKDAYS = [
    { v: "0", label: "อาทิตย์" },
    { v: "1", label: "จันทร์" },
    { v: "2", label: "อังคาร" },
    { v: "3", label: "พุธ" },
    { v: "4", label: "พฤหัสบดี" },
    { v: "5", label: "ศุกร์" },
    { v: "6", label: "เสาร์" }
  ];
  const dayLabel = (d: number | null) => WEEKDAYS.find(w => w.v === String(d))?.label ?? "";

  // Collect active filter tags
  const tags: Array<{ key: string; label: string; value: string; onRemove: () => void }> = [];

  const addTags = (key: string, label: string, values: string[], filterKey: keyof FiltersType) => {
    values.forEach(v => {
      tags.push({
        key: `${key}-${v}`,
        label,
        value: v,
        onRemove: () => onUpdateFilters({ [filterKey]: (filters[filterKey] as string[]).filter(x => x !== v) })
      });
    });
  };

  if (filters.search)
    tags.push({
      key: "search",
      label: "ค้นหา",
      value: filters.search,
      onRemove: () => {
        onUpdateFilters({ search: "" });
        setSearchInput("");
      }
    });
  addTags("subject", "วิชา", filters.subjects, "subjects");
  addTags("stage", "ระดับ", filters.educationStages, "educationStages");
  addTags("course", "ประเภท", filters.courseTypes, "courseTypes");
  filters.interSubTypes.forEach(v => {
    const lbl = v === "EN_EN" ? "เนื้อหา EN / สื่อสาร EN" : v === "EN_TH" ? "เนื้อหา EN / สื่อสาร TH" : v;
    tags.push({
      key: `interSub-${v}`,
      label: "การสื่อสาร",
      value: lbl,
      onRemove: () => onUpdateFilters({ interSubTypes: filters.interSubTypes.filter(x => x !== v) })
    });
  });
  addTags("spec", "เฉพาะทาง", filters.specialized, "specialized");
  addTags("style", "สไตล์", filters.teachingStyles, "teachingStyles");
  filters.styleLevels.forEach(v => {
    tags.push({
      key: `styleLevel-${v}`,
      label: "ระดับสไตล์",
      value: `Lv.${v}`,
      onRemove: () => onUpdateFilters({ styleLevels: filters.styleLevels.filter(x => x !== v) })
    });
  });
  addTags("gender", "เพศ", filters.gender, "gender");
  addTags("pers", "บุคลิกภาพ", filters.personality, "personality");
  addTags("trial", "คลาสทดลองเรียน", filters.trialLesson, "trialLesson");
  addTags("branch", "สาขา", filters.branches, "branches");
  addTags("prof", "ระดับนักเรียน", filters.studentProficiency, "studentProficiency");
  if (filters.status !== "ACTIVE")
    tags.push({ key: "status", label: "สถานะ", value: filters.status, onRemove: () => onUpdateFilters({ status: "ACTIVE" }) });
  if (filters.university)
    tags.push({
      key: "university",
      label: "มหาวิทยาลัย",
      value: filters.university,
      onRemove: () => {
        onUpdateFilters({ university: "" });
        setUniversityInput("");
      }
    });
  if (filters.major)
    tags.push({
      key: "major",
      label: "คณะ/สาขา",
      value: filters.major,
      onRemove: () => {
        onUpdateFilters({ major: "" });
        setMajorInput("");
      }
    });
  if (filters.selectedStudentCode) {
    const lbl = selectedStudent
      ? `${selectedStudent.code} – ${selectedStudent.nickname || selectedStudent.first_name}`
      : filters.selectedStudentCode;
    tags.push({ key: "student", label: "นักเรียน", value: lbl, onRemove: () => onUpdateFilters({ selectedStudentCode: "" }) });
  }
  if (filters.selectedClassTrialId) {
    const lbl = selectedTrial
      ? `${selectedTrial.student_firstname || selectedTrial.student_name || "(ไม่มีชื่อ)"}${selectedTrial.student_nickname ? ` (${selectedTrial.student_nickname})` : ""}`
      : filters.selectedClassTrialId;
    tags.push({
      key: "trial",
      label: "ทดลองเรียน",
      value: lbl,
      onRemove: () => onUpdateFilters({ selectedClassTrialId: "", studentMode: "none" })
    });
  }
  if (filters.selectedStudentDisplayName) {
    tags.push({
      key: "manualName",
      label: "นักเรียน (พิมพ์เอง)",
      value: filters.selectedStudentDisplayName,
      onRemove: () => onUpdateFilters({ selectedStudentDisplayName: "", studentMode: "none" })
    });
  }
  if (filters.taughtSelectedStudent !== "any") {
    tags.push({
      key: "taught",
      label: "เคยสอนนักเรียนนี้",
      value: filters.taughtSelectedStudent === "yes" ? "เคย" : "ไม่เคย",
      onRemove: () => onUpdateFilters({ taughtSelectedStudent: "any" })
    });
  }
  filters.availabilitySlots.forEach((slot, idx) => {
    const time = slot.start && slot.end ? ` ${slot.start}-${slot.end}` : "";
    tags.push({
      key: `slot-${idx}`,
      label: "ว่าง",
      value: `${dayLabel(slot.day)}${time}`,
      onRemove: () =>
        onUpdateFilters({
          availabilitySlots: filters.availabilitySlots.filter((_, i) => i !== idx)
        })
    });
  });

  const updateSlot = (idx: number, patch: Partial<AvailabilitySlot>) => {
    onUpdateFilters({
      availabilitySlots: filters.availabilitySlots.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    });
  };
  const addSlot = () => {
    onUpdateFilters({
      availabilitySlots: [...filters.availabilitySlots, { day: 1, start: "", end: "" }]
    });
  };
  const removeSlot = (idx: number) => {
    onUpdateFilters({
      availabilitySlots: filters.availabilitySlots.filter((_, i) => i !== idx)
    });
  };

  return (
    <div className="space-y-4">
      {/* Student picker + Availability row */}
      <div className="flex flex-wrap gap-3 items-end p-3 bg-muted/30 rounded-lg border">
        <div className="flex flex-col space-y-1.5 w-full">
          <Label className="text-xs text-muted-foreground">นักเรียน (เลือก 1 โหมด)</Label>
          <Tabs
            value={filters.studentMode === "none" ? "system" : filters.studentMode}
            onValueChange={v => {
              const mode = v as "system" | "trial" | "manual";
              onUpdateFilters({
                studentMode: mode,
                selectedStudentCode: mode === "system" ? filters.selectedStudentCode : "",
                selectedClassTrialId: mode === "trial" ? filters.selectedClassTrialId : "",
                selectedStudentDisplayName: mode === "manual" ? filters.selectedStudentDisplayName : "",
                taughtSelectedStudent: mode === "system" ? filters.taughtSelectedStudent : "any"
              });
            }}
            className="w-full"
          >
            <TabsList className="grid grid-cols-3 w-full max-w-md">
              <TabsTrigger value="system">ในระบบ</TabsTrigger>
              <TabsTrigger value="trial">ทดลองเรียน</TabsTrigger>
              <TabsTrigger value="manual">พิมพ์เอง</TabsTrigger>
            </TabsList>

            <TabsContent value="system" className="mt-2">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col space-y-1.5">
                  <Popover open={studentOpen} onOpenChange={setStudentOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[280px] justify-start font-normal">
                        <User className="h-4 w-4 mr-2 opacity-60" />
                        {selectedStudent ? (
                          `${selectedStudent.code} – ${selectedStudent.first_name}${selectedStudent.nickname ? ` (${selectedStudent.nickname})` : ""}`
                        ) : filters.selectedStudentCode ? (
                          filters.selectedStudentCode
                        ) : (
                          <span className="text-muted-foreground">ค้นหานักเรียนในระบบ...</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[340px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput placeholder="รหัส / ชื่อ / ชื่อเล่น..." value={studentQuery} onValueChange={setStudentQuery} />
                        <CommandList>
                          {studentLoading && <div className="p-2 text-sm text-muted-foreground">กำลังค้นหา...</div>}
                          {!studentLoading && studentQuery && studentResults.length === 0 && <CommandEmpty>ไม่พบนักเรียน</CommandEmpty>}
                          <CommandGroup>
                            {studentResults.map(s => (
                              <CommandItem
                                key={s.code}
                                value={s.code}
                                onSelect={() => {
                                  onUpdateFilters({
                                    selectedStudentCode: s.code,
                                    studentMode: "system",
                                    selectedClassTrialId: "",
                                    selectedStudentDisplayName: ""
                                  });
                                  setStudentOpen(false);
                                  setStudentQuery("");
                                }}
                              >
                                <span className="font-mono text-xs mr-2">{s.code}</span>
                                {s.first_name} {s.last_name || ""}
                                {s.nickname && <span className="ml-1 text-muted-foreground">({s.nickname})</span>}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">เคยสอนนักเรียนนี้</Label>
                  <Select
                    value={filters.taughtSelectedStudent}
                    onValueChange={v => onUpdateFilters({ taughtSelectedStudent: v as "any" | "yes" | "no" })}
                    disabled={!filters.selectedStudentCode}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">ไม่กรอง</SelectItem>
                      <SelectItem value="yes">เคยสอน</SelectItem>
                      <SelectItem value="no">ไม่เคยสอน</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="trial" className="mt-2">
              <Popover open={trialOpen} onOpenChange={setTrialOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[320px] justify-start font-normal">
                    <User className="h-4 w-4 mr-2 opacity-60" />
                    {selectedTrial ? (
                      `${selectedTrial.student_firstname || selectedTrial.student_name || "(ไม่มีชื่อ)"}${selectedTrial.student_nickname ? ` (${selectedTrial.student_nickname})` : ""}`
                    ) : filters.selectedClassTrialId ? (
                      filters.selectedClassTrialId
                    ) : (
                      <span className="text-muted-foreground">ค้นหานักเรียนทดลองเรียน...</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[360px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="ชื่อ / ชื่อเล่น..." value={trialQuery} onValueChange={setTrialQuery} />
                    <CommandList>
                      {trialLoading && <div className="p-2 text-sm text-muted-foreground">กำลังค้นหา...</div>}
                      {!trialLoading && trialQuery && trialResults.length === 0 && <CommandEmpty>ไม่พบนักเรียนทดลองเรียน</CommandEmpty>}
                      <CommandGroup>
                        {trialResults.map((s: any) => (
                          <CommandItem
                            key={s.id}
                            value={s.id}
                            onSelect={() => {
                              onUpdateFilters({
                                selectedClassTrialId: s.id,
                                studentMode: "trial",
                                selectedStudentCode: "",
                                selectedStudentDisplayName: "",
                                taughtSelectedStudent: "any"
                              });
                              setTrialOpen(false);
                              setTrialQuery("");
                            }}
                          >
                            {s.student_firstname || s.student_name || "(ไม่มีชื่อ)"}
                            {s.student_nickname && <span className="ml-1 text-muted-foreground">({s.student_nickname})</span>}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </TabsContent>

            <TabsContent value="manual" className="mt-2">
              <Input
                className="w-[320px]"
                placeholder="พิมพ์ชื่อนักเรียน เช่น น้องอาร์ต (อาร์ต)"
                value={filters.selectedStudentDisplayName}
                onChange={e =>
                  onUpdateFilters({
                    selectedStudentDisplayName: e.target.value,
                    studentMode: e.target.value ? "manual" : "none",
                    selectedStudentCode: "",
                    selectedClassTrialId: "",
                    taughtSelectedStudent: "any"
                  })
                }
              />
            </TabsContent>
          </Tabs>
        </div>
        <div className="space-y-2 w-full">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">
              วัน-เวลาว่าง <span className="text-destructive">*</span> (เพิ่มได้หลายช่วง)
            </Label>
            <Button type="button" variant="outline" size="sm" onClick={addSlot}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              เพิ่มช่วงเวลา
            </Button>
          </div>
          {filters.availabilitySlots.length === 0 && <p className="text-xs text-muted-foreground">ยังไม่ได้เลือกวัน-เวลา (กรองครูทุกคน)</p>}
          <div className="space-y-2">
            {filters.availabilitySlots.map((slot, idx) => {
              const invalid = slot.start && slot.end && slot.end <= slot.start;
              return (
                <div key={idx} className="flex flex-wrap items-center gap-2">
                  <Select value={String(slot.day)} onValueChange={v => updateSlot(idx, { day: parseInt(v, 10) })}>
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map(w => (
                        <SelectItem key={w.v} value={w.v}>
                          {w.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="time" className="w-[120px]" value={slot.start} onChange={e => updateSlot(idx, { start: e.target.value })} />
                  <span className="text-xs text-muted-foreground">ถึง</span>
                  <Input type="time" className="w-[120px]" value={slot.end} onChange={e => updateSlot(idx, { end: e.target.value })} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => removeSlot(idx)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  {invalid && <span className="text-xs text-destructive">เวลาเลิกต้องมากกว่าเวลาเริ่ม</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search + Status row */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="ค้นหาชื่อ, รหัส, ชื่อเล่น..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filters.status} onValueChange={v => onUpdateFilters({ status: v })}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="สถานะ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">ทั้งหมด</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="มหาวิทยาลัย..."
          value={universityInput}
          onChange={e => setUniversityInput(e.target.value)}
          className="w-[180px]"
        />
        <Input placeholder="คณะ/สาขา..." value={majorInput} onChange={e => setMajorInput(e.target.value)} className="w-[180px]" />
      </div>

      {/* Dropdown filters (for large option sets) */}
      <div className="flex flex-wrap gap-2">
        <MultiSelectFilter
          required
          label="วิชา"
          options={filterOptions.subjects}
          selected={filters.subjects}
          onChange={v => onUpdateFilters({ subjects: v })}
        />
        <MultiSelectFilter
          required
          label="ระดับชั้น"
          options={filterOptions.educationStages}
          selected={filters.educationStages}
          onChange={v => onUpdateFilters({ educationStages: v })}
        />
        <MultiSelectFilter
          label="เฉพาะทาง"
          options={filterOptions.specialized}
          selected={filters.specialized}
          onChange={v => onUpdateFilters({ specialized: v })}
        />
        <MultiSelectFilter
          label="สไตล์การสอน"
          options={filterOptions.teachingStyles}
          selected={filters.teachingStyles}
          onChange={v => onUpdateFilters({ teachingStyles: v })}
        />
        <MultiSelectFilter
          label="บุคลิกภาพ (MBTI)"
          options={filterOptions.personality}
          selected={filters.personality}
          onChange={v => onUpdateFilters({ personality: v })}
        />
        <MultiSelectFilter
          required
          label="สาขา"
          options={filterOptions.branches}
          selected={filters.branches}
          onChange={v => onUpdateFilters({ branches: v })}
        />
        <MultiSelectFilter
          label="ระดับนักเรียน"
          options={filterOptions.studentProficiency}
          selected={filters.studentProficiency}
          onChange={v => onUpdateFilters({ studentProficiency: v })}
        />
      </div>

      {/* AND-logic explanation */}
      <div className="rounded-md border border-dashed border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground space-y-1.5">
        <div className="font-semibold text-foreground">การกรองใช้หลัก AND (ต้องตรงทุกเงื่อนไขในแถวเดียวกัน)</div>
        <div>
          • <span className="font-medium text-foreground">วิชา + ระดับชั้น + Regular/Inter</span> ต้องอยู่ในแถวเดียวกันของครู
          <div className="ml-3 text-[11px]">
            ตัวอย่าง: เลือก <em>คณิต + ฟิสิกส์</em> และ <em>Inter</em> → จะแสดงเฉพาะครูที่สอน <em>คณิต Inter</em> และ <em>ฟิสิกส์ Inter</em>{" "}
            ทั้งคู่ (ครูที่สอนคณิตประถมไทย + ฟิสิกส์ม.ปลาย Inter จะไม่ถูกแสดง)
          </div>
        </div>
        <div>
          • <span className="font-medium text-foreground">สไตล์การสอน + ระดับ</span> ครูต้องมีทุกสไตล์ที่เลือก โดยแต่ละสไตล์ต้องมีระดับ ≥
          ค่าต่ำสุดที่เลือก
          <div className="ml-3 text-[11px]">
            ตัวอย่าง: เลือก <em>กวดวิชา + ติว</em> และระดับ <em>2</em> → ครูต้องมีทั้ง <em>กวดวิชา ≥2</em> และ <em>ติว ≥2</em>
          </div>
        </div>
      </div>

      {/* Inline checkbox filters (for small option sets) */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-foreground uppercase tracking-wide">คุณสมบัติครู</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* เพศ */}
          <div className="rounded-md border bg-background p-3 space-y-2">
            <div className="text-xs font-semibold text-foreground border-b pb-2">เพศ</div>
            <CheckboxGroupFilter
              label="เพศ"
              hideLabel
              orientation="vertical"
              options={filterOptions.gender}
              selected={filters.gender}
              onChange={v => onUpdateFilters({ gender: v })}
            />
          </div>

          {/* Regular/Inter + Inter sub */}
          <div className="rounded-md border bg-background p-3 space-y-2">
            <div className="text-xs font-semibold text-foreground border-b pb-2">
              Regular / Inter <span className="text-destructive">*</span>
            </div>
            <CheckboxGroupFilter
              label="Regular/Inter"
              hideLabel
              orientation="vertical"
              // Force "Regular" first, "Inter" second (other values keep alphabetical)
              options={[...filterOptions.courseTypes].sort((a, b) => {
                if (a === "Regular") return -1;
                if (b === "Regular") return 1;
                if (a === "Inter") return -1;
                if (b === "Inter") return 1;
                return a.localeCompare(b);
              })}
              selected={filters.courseTypes}
              onChange={v =>
                onUpdateFilters({
                  courseTypes: v,
                  interSubTypes: v.includes("Inter") ? filters.interSubTypes : []
                })
              }
            />
            {filters.courseTypes.includes("Inter") && (
              <div className="border-l-2 border-primary/40 pl-3 ml-1 mt-2 space-y-1.5">
                <span className="text-[11px] font-medium text-muted-foreground">
                  การสื่อสาร <span className="text-destructive">*</span>
                </span>
                <div className="flex flex-col gap-1.5">
                  {[
                    { v: "EN_EN", label: "เนื้อหา EN / สื่อสาร EN" },
                    { v: "EN_TH", label: "เนื้อหา EN / สื่อสาร TH" }
                  ].map(opt => {
                    const checked = filters.interSubTypes.includes(opt.v);
                    return (
                      <label key={opt.v} className="flex items-center gap-1.5 cursor-pointer text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() =>
                            onUpdateFilters({
                              interSubTypes: checked ? filters.interSubTypes.filter(x => x !== opt.v) : [...filters.interSubTypes, opt.v]
                            })
                          }
                        />
                        <span className={checked ? "text-foreground font-medium" : "text-muted-foreground"}>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* คลาสทดลองเรียน */}
          <div className="rounded-md border bg-background p-3 space-y-2">
            <div className="text-xs font-semibold text-foreground border-b pb-2">คลาสทดลองเรียน</div>
            <CheckboxGroupFilter
              label="คลาสทดลองเรียน"
              hideLabel
              orientation="vertical"
              options={filterOptions.trialLesson}
              selected={filters.trialLesson}
              onChange={v => onUpdateFilters({ trialLesson: v })}
            />
          </div>

          {/* ระดับสไตล์การสอน */}
          <div className="rounded-md border bg-background p-3 space-y-2">
            <div className="text-xs font-semibold text-foreground border-b pb-2">ระดับสไตล์การสอน</div>
            <NumberCheckboxGroupFilter
              label="ระดับสไตล์การสอน"
              hideLabel
              orientation="vertical"
              options={filterOptions.styleLevels}
              selected={filters.styleLevels}
              onChange={v => onUpdateFilters({ styleLevels: v })}
              renderLabel={v => `Lv.${v}`}
            />
          </div>
        </div>
      </div>

      {/* Active filter tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">ตัวกรอง:</span>
          {tags.map(tag => (
            <Badge key={tag.key} variant="secondary" className="gap-1 pr-1">
              <span className="text-xs text-muted-foreground">{tag.label}:</span>
              <span className="text-xs font-medium">{tag.value}</span>
              <button onClick={tag.onRemove} className="ml-0.5 hover:bg-muted rounded-full p-0.5">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onClearFilters();
              setSearchInput("");
              setUniversityInput("");
              setMajorInput("");
            }}
            className="text-xs h-7"
          >
            <FilterX className="h-3 w-3 mr-1" />
            ล้างทั้งหมด
          </Button>
        </div>
      )}

      {/* Result count */}
      <div className="text-sm text-muted-foreground">
        แสดง <span className="font-semibold text-foreground">{resultCount}</span> จาก {totalCount} ครู
      </div>
    </div>
  );
};
